import { FilterFieldCapability } from './baseFilterTypes'

// Operators the Kysely executor runs, per field kind — the single source of truth: the scalar/relation
// builders execute exactly these, and the FE keeps its offered operators within them (operator-subset
// guard) so v2 never silently drops one. FE-safe: string data only, no Kysely/DB imports.
export const SCALAR_TYPE_OPERATORS = {
  string: ['equals', 'doesNotEqual', 'contains', 'doesNotContain', 'startsWith', 'endsWith', 'isEmpty', 'isNotEmpty', 'isAnyOf'],
  boolean: ['equals', 'doesNotEqual', 'is', 'isEmpty', 'isNotEmpty'],
  number: ['=', '!=', '>', '<', '>=', '<=', 'isEmpty', 'isNotEmpty'],
  date: ['is', 'not', 'before', 'after', 'onOrBefore', 'onOrAfter', 'isEmpty', 'isNotEmpty'],
  timestamp: ['is', 'not', 'before', 'after', 'onOrBefore', 'onOrAfter', 'isEmpty', 'isNotEmpty'],
  uuid: ['equals', 'doesNotEqual', 'isAnyOf', 'isEmpty', 'isNotEmpty'],
  enum: ['equals', 'doesNotEqual', 'isAnyOf', 'isEmpty', 'isNotEmpty'],
  json: ['equals', 'doesNotEqual', 'isEmpty', 'isNotEmpty'],
} as const satisfies Record<NonNullable<FilterFieldCapability['scalarType']>, readonly string[]>

export const RELATION_OPERATORS = [
  'equals',
  'doesNotEqual',
  'contains',
  'doesNotContain',
  'startsWith',
  'endsWith',
  'isAnyOf',
  'isEmpty',
  'isNotEmpty',
] as const

// Default for computed fields with no computedKind: substring `contains` only. Not every service's plain
// computed handler negates safely, so richer operators are opted into per field via computedKind
// (COMPUTED_CATEGORY_OPERATORS / COMPUTED_AUTO_GENERATED_TAGS_OPERATORS / COMPUTED_PERSON_OPERATORS), never
// added to this shared default.
export const COMPUTED_OPERATORS = ['contains'] as const

// People computed fields (createdBy/assignees/owner/businessOwner/budgetHolder) serve substring `contains`
// and exact full-name `equals` with their `doesNotContain`/`doesNotEqual` negations, `isAnyOf` (an OR of
// exact full-name matches), plus value-less isEmpty/isNotEmpty presence checks.
export const COMPUTED_PERSON_OPERATORS = [
  'contains',
  'doesNotContain',
  'equals',
  'doesNotEqual',
  'isAnyOf',
  'isEmpty',
  'isNotEmpty',
] as const

// category substring-matches a coalesced option table, so it serves `contains` and its `doesNotContain`
// negation. Kept off COMPUTED_OPERATORS (see there) so only this field, whose handler negates safely, gets it.
export const COMPUTED_CATEGORY_OPERATORS = ['contains', 'doesNotContain'] as const

// autoGeneratedTags resolves tag_ids to condition-group names via an EXISTS, so it serves `contains` and its
// `doesNotContain` negation (NULL-safe: a tag-less request matches the negation). Kept off COMPUTED_OPERATORS
// (see there) so only this field, whose handler negates safely, gets it.
export const COMPUTED_AUTO_GENERATED_TAGS_OPERATORS = ['contains', 'doesNotContain'] as const

// `satisfies Record<NonNullable<…>>` makes a newly-added computedKind a compile error here until its
// operator set is chosen — so a new kind can't silently inherit the contains-only default.
const COMPUTED_OPERATORS_BY_KIND = {
  person: COMPUTED_PERSON_OPERATORS,
  category: COMPUTED_CATEGORY_OPERATORS,
  autoGeneratedTags: COMPUTED_AUTO_GENERATED_TAGS_OPERATORS,
} satisfies Record<NonNullable<FilterFieldCapability['computedKind']>, readonly string[]>

export const getSupportedOperators = (capability: FilterFieldCapability): readonly string[] => {
  switch (capability.type) {
    case 'scalar':
      return SCALAR_TYPE_OPERATORS[capability.scalarType ?? 'string']
    case 'relation':
      return RELATION_OPERATORS
    case 'computed':
      return capability.computedKind ? COMPUTED_OPERATORS_BY_KIND[capability.computedKind] : COMPUTED_OPERATORS
    case 'custom':
      return []
    default: {
      // FE-safe file, so we can't use the backend `assertNever` (it imports the lambda logger); an
      // inline never-check turns a future capability type into a compile error at this exact spot.
      const exhaustive: never = capability.type
      return exhaustive
    }
  }
}
